import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Card, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Button, IconButton,
  Tooltip, Stack, Typography, FormControlLabel, Switch, Chip, Alert,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PageHeader } from "../components/PageHeader.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi.js";
import { formLabel } from "../lib/labels.js";
import type { AcquiredService } from "@kappa-v2/contracts";

// Servizi Acquisiti = CATALOGO di voci di costo (allineato alla produzione:
// name/description/serviceType/genericType/price/emailNotification), referenziato dai Costi.
// In Fase 5: select i18n per serviceType/genericType e collegamento ai Costi.

interface FormValues {
  name: string; description: string; serviceType: string;
  genericType: string; price: number; emailNotification: boolean;
}
const EMPTY: FormValues = {
  name: "", description: "", serviceType: "form.service.costs.institutional",
  genericType: "form.service.acquired.generic1", price: 0, emailNotification: false
};

// Opzioni allineate alla v1 (EnumServiceAcquiredType / EnumServiceAcquiredGenericsType).
const SERVICE_TYPE_OPTIONS = [
  "form.service.costs.institutional",
  "form.service.costs.commercial",
  "form.service.costs.promiscuous",
  "form.service.costs.other"
];
const GENERIC_TYPE_OPTIONS = [
  "form.service.acquired.generic1",
  "form.service.acquired.generic2",
  "form.service.acquired.generic3",
  "form.service.acquired.generic4",
  "form.service.acquired.generic5",
  "form.service.acquired.generic6"
];

export function ServicesAcquiredPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const schema = Yup.object({
    name: Yup.string().required(t("servicesAcquired.form.nameRequired")),
    serviceType: Yup.string().required(t("servicesAcquired.form.typeRequired")),
    genericType: Yup.string().required(t("servicesAcquired.form.genericRequired")),
    price: Yup.number().min(0).required(t("servicesAcquired.form.priceRequired")),
    description: Yup.string(),
    emailNotification: Yup.boolean()
  });
  const [rows, setRows] = useState<AcquiredService[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AcquiredService | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AcquiredService | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<{ data: AcquiredService[] } | AcquiredService[]>("/api/v1/acqservices")
      .then((v) => setRows(Array.isArray(v) ? v : (v.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        if (editTarget?._id) {
          await call(`/api/v1/acqservices/${editTarget._id}`, { method: "PUT", body: values });
        } else {
          await call("/api/v1/acqservices", { method: "POST", body: values });
        }
        setDialogOpen(false); fetchAll();
      } finally { setSaving(false); }
    }
  });

  const openAdd = () => { setEditTarget(null); formik.resetForm({ values: EMPTY }); setDialogOpen(true); };
  const openEdit = (a: AcquiredService) => {
    setEditTarget(a);
    formik.resetForm({ values: {
      name: a.name, description: a.description ?? "", serviceType: a.serviceType,
      genericType: a.genericType, price: a.price, emailNotification: a.emailNotification
    }});
    setDialogOpen(true);
  };
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/acqservices/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null); fetchAll();
  };

  const filtered = useMemo(() => (
    rows.filter((r) => `${r.name} ${r.description ?? ""}`.toLowerCase().includes(search.toLowerCase()))
  ), [rows, search]);

  const columns: GridColDef[] = [
    {
      field: "name", headerName: t("servicesAcquired.nameCol"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<AcquiredService>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{params.row.name}</Typography>
          {params.row.description &&
            <Typography variant="caption" color="text.secondary" noWrap>{params.row.description}</Typography>}
        </Box>
      )
    },
    {
      field: "serviceType", headerName: t("servicesAcquired.type"), width: 200,
      renderCell: (params: GridRenderCellParams<AcquiredService>) => (
        <Chip size="small" label={formLabel(params.row.serviceType)} variant="outlined" sx={{ fontSize: "0.7rem" }} />
      )
    },
    {
      field: "price", headerName: t("servicesAcquired.price"), width: 120,
      renderCell: (params: GridRenderCellParams<AcquiredService>) => (
        <Typography variant="body2" fontWeight={700}>
          € {(params.row.price ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false,
      renderCell: (params: GridRenderCellParams<AcquiredService>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t("common.edit")}>
            <IconButton size="small" onClick={() => openEdit(params.row)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("servicesAcquired.title")} subtitle={t("servicesAcquired.subtitle")}
        actionLabel={t("servicesAcquired.addItem")} onAction={openAdd} onRefresh={fetchAll} />
      <Box sx={{ p: 3, flex: 1 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("servicesAcquired.notice")}
        </Alert>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
            <TextField size="small" placeholder={t("servicesAcquired.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("servicesAcquired.none")} description={t("servicesAcquired.noneDesc")} actionLabel={t("servicesAcquired.addItem")} onAction={openAdd} />
            : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                loading={loading} autoHeight rowHeight={56} pageSizeOptions={[25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                disableRowSelectionOnClick sx={{ border: "none" }}
              />
          }
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("servicesAcquired.editItem") : t("servicesAcquired.addItem")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.acqservices") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t("servicesAcquired.form.nameLabel")}
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t("servicesAcquired.form.descriptionLabel")} multiline rows={2}
                  {...formik.getFieldProps("description")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small"
                  error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}>
                  <InputLabel id="acq-type-label">{t("servicesAcquired.form.typeLabel")}</InputLabel>
                  <Select labelId="acq-type-label" label={t("servicesAcquired.form.typeLabel")}
                    {...formik.getFieldProps("serviceType")}>
                    {SERVICE_TYPE_OPTIONS.map((value) => (
                      <MenuItem key={value} value={value}>{formLabel(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small"
                  error={formik.touched.genericType && Boolean(formik.errors.genericType)}>
                  <InputLabel id="acq-generic-label">{t("servicesAcquired.form.genericLabel")}</InputLabel>
                  <Select labelId="acq-generic-label" label={t("servicesAcquired.form.genericLabel")}
                    {...formik.getFieldProps("genericType")}>
                    {GENERIC_TYPE_OPTIONS.map((value) => (
                      <MenuItem key={value} value={value}>{formLabel(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t("servicesAcquired.form.priceLabel")} type="number"
                  {...formik.getFieldProps("price")} inputProps={{ step: 0.01, min: 0 }}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={formik.values.emailNotification}
                    onChange={(e) => formik.setFieldValue("emailNotification", e.target.checked)} />}
                  label={t("servicesAcquired.emailNotification")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : editTarget ? t("common.save") : t("servicesAcquired.form.submitAdd")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("servicesAcquired.deleteConfirm", { name: deleteTarget?.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
